import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";

const API_BASE =
  process.env.REACT_APP_SSB_API_BASE ||
  (window.location.hostname === "localhost"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");

const money0 = (n) =>
  n == null || Number.isNaN(Number(n))
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(n));

const pct1 = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(1)}%`);
const short = (s) => (s || "").slice(0, 10);

const Pill = ({ text, tone = "neutral" }) => {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
    info: { bg: "#EFF8FF", fg: "#175CD3" },
  };
  const c = map[tone] || map.neutral;
  return (
    <span style={{
      padding: "4px 10px",
      borderRadius: 999,
      background: c.bg,
      color: c.fg,
      fontSize: 12,
      fontWeight: 900,
      border: "1px solid rgba(0,0,0,0.04)",
      letterSpacing: 0.2,
      whiteSpace: "nowrap"
    }}>
      {text}
    </span>
  );
};

const scoreTone = (s) => (s >= 2 ? "bad" : s >= 1 ? "warn" : "good");
const ratingTone = (r) => (r === "HIGH" ? "bad" : r === "MEDIUM" ? "warn" : "good");

function buildPeriodMeta(months, periodType) {
  if (periodType === "month") {
    return months.map((m) => ({ label: short(m), month: m }));
  }
  const map = new Map();
  for (const m of months) {
    const year = m.slice(0, 4);
    const q = Math.ceil(parseInt(m.slice(5, 7)) / 3);
    const key = `${year}-Q${q}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(m);
  }
  return Array.from(map.entries()).map(([label, ms]) => ({ label, month: ms[0] }));
}

const PERIOD_TYPES = ["month", "quarter"];

export default function SSBPortfolioRiskHeatmap() {
  const [months, setMonths] = useState([]);
  const [periodType, setPeriodType] = useState("month");
  const [selectedPeriod, setSelectedPeriod] = useState("");

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  const periodMeta = useMemo(() => buildPeriodMeta(months, periodType), [months, periodType]);

  // Load filters
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/v1/ssb/filters/portfolio-risk-heatmap`);
        const json = await resp.json();
        if (!mounted) return;
        setMonths(Array.isArray(json.months) ? json.months : []);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Initialize selectedPeriod when periodMeta changes
  useEffect(() => {
    if (periodMeta.length > 0) {
      setSelectedPeriod((prev) => {
        const stillValid = periodMeta.some((m) => m.label === prev);
        return stillValid ? prev : periodMeta[0].label;
      });
    }
  }, [periodMeta]);

  // Load report
  useEffect(() => {
    if (!selectedPeriod || periodMeta.length === 0) return;

    const meta = periodMeta.find((m) => m.label === selectedPeriod);
    if (!meta) return;

    let mounted = true;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const url = new URL(`${API_BASE}/v1/ssb/portfolio/risk-heatmap`);
        url.searchParams.set("month_start", meta.month);
        const resp = await fetch(url.toString());
        const json = await resp.json();
        if (!resp.ok) throw new Error(json?.detail || json?.error || `HTTP ${resp.status}`);
        if (!mounted) return;
        setRows(Array.isArray(json.rows) ? json.rows : []);
      } catch (e) {
        console.error(e);
        if (mounted) setErr(`Failed to load heatmap: ${e.message || e}`);
        if (mounted) setRows([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [selectedPeriod, periodMeta]);

  const summary = useMemo(() => {
    const total = rows.reduce((s, r) => s + Number(r.month_total_amount || 0), 0);
    const top3 = rows.slice(0, 3).reduce((s, r) => s + Number(r.month_total_amount || 0), 0);
    const top3Pct = total > 0 ? (top3 / total) * 100 : 0;
    const highCount = rows.filter(r => r.rating === "HIGH").length;
    return { total, top3Pct, highCount };
  }, [rows]);

  const toggleStyle = (active) => ({
    padding: "3px 9px",
    borderRadius: 6,
    border: `1px solid ${active ? "#3730A3" : "#D7DEE9"}`,
    background: active ? "#EEF2FF" : "#fff",
    color: active ? "#3730A3" : "#64748B",
    fontSize: 11,
    fontWeight: active ? 800 : 500,
    cursor: "pointer",
    lineHeight: 1.4,
  });

  return (
    <SSBReportShell
      title="SSB Dashboard — Portfolio Risk Heatmap"
      subtitle={`Period: ${selectedPeriod || "—"} • Programs: ${rows.length || "—"}`}
      rightMeta={[loading ? "Loading…" : "Live", "Phase 1 (No AML engine)"]}
      leftMeta={["Sponsor bank oversight", "Composite risk posture across programs"]}
      footerLeft="Idenza • SSB Reporting (Phase 1)"
      footerRight="Sources: program_monthly_mix + ticket_bands + country_exposure + counterparty_concentration"
    >
      <SSBFiltersBar columns={4}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>Period</div>
            <div style={{ display: "flex", gap: 3 }}>
              {PERIOD_TYPES.map((t) => (
                <button key={t} onClick={() => setPeriodType(t)} style={toggleStyle(periodType === t)}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            {periodMeta.map((m) => <option key={m.label} value={m.label}>{m.label}</option>)}
          </select>
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <Pill text={`Top 3 concentration: ${pct1(summary.top3Pct)}`} tone={summary.top3Pct >= 80 ? "bad" : summary.top3Pct >= 65 ? "warn" : "good"} />
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          <Pill text={`High-risk programs: ${summary.highCount}`} tone={summary.highCount >= 3 ? "warn" : "good"} />
        </div>

        <div style={{ display: "flex", alignItems: "end" }}>
          {loading ? <Pill text="Loading…" tone="neutral" /> : <Pill text="Live" tone="good" />}
        </div>
      </SSBFiltersBar>

      {err ? (
        <div style={{ background: "#FEF3F2", border: "1px solid #FECACA", color: "#7F1D1D", padding: 12, borderRadius: 16, marginBottom: 12 }}>
          {err}
        </div>
      ) : null}

      <div style={{
        background: "#fff",
        border: "1px solid #E7ECF3",
        borderRadius: 16,
        padding: 12,
        boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      }}>
        <div style={{ fontWeight: 950, color: "#0B1F33", marginBottom: 8 }}>
          Programs ranked by composite risk score
        </div>

        <div style={{ overflowX: "auto", border: "1px solid #F1F5F9", borderRadius: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid #E7ECF3", background: "#F8FAFC" }}>
                <th style={{ padding: 10 }}>Program</th>
                <th style={{ padding: 10 }}>{periodType === "quarter" ? "Quarterly Volume" : "Monthly Volume"}</th>
                <th style={{ padding: 10 }}>% Portfolio</th>
                <th style={{ padding: 10 }}>Top5 CP Share</th>
                <th style={{ padding: 10 }}>Non-US Share</th>
                <th style={{ padding: 10 }}>50k+ Share</th>
                <th style={{ padding: 10 }}>Signals</th>
                <th style={{ padding: 10 }}>Risk</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid #F1F5F9" }}>
                  <td style={{ padding: 10, fontWeight: 900, color: "#0B1F33" }}>{r.program_name}</td>
                  <td style={{ padding: 10 }}>{money0(r.month_total_amount)}</td>
                  <td style={{ padding: 10 }}>{pct1(r.pct_of_portfolio)}</td>
                  <td style={{ padding: 10 }}>{pct1(r.top5_counterparty_share_pct)}</td>
                  <td style={{ padding: 10 }}>{pct1(r.non_us_volume_share_pct)}</td>
                  <td style={{ padding: 10 }}>{pct1(r.amt_50k_plus_share_pct)}</td>
                  <td style={{ padding: 10 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <Pill text={`Vol ${r.scores?.volume ?? "—"}`} tone={scoreTone(r.scores?.volume)} />
                      <Pill text={`Cpty ${r.scores?.concentration ?? "—"}`} tone={scoreTone(r.scores?.concentration)} />
                      <Pill text={`XB ${r.scores?.cross_border ?? "—"}`} tone={scoreTone(r.scores?.cross_border)} />
                      <Pill text={`HV ${r.scores?.high_value ?? "—"}`} tone={scoreTone(r.scores?.high_value)} />
                      <Pill text={`Σ ${r.scores?.total ?? "—"}`} tone={r.scores?.total >= 6 ? "bad" : r.scores?.total >= 3 ? "warn" : "good"} />
                    </div>
                  </td>
                  <td style={{ padding: 10 }}>
                    <Pill text={r.rating || "—"} tone={ratingTone(r.rating)} />
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 12, color: "#64748B" }}>No data for selected period.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </SSBReportShell>
  );
}